import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TeacherAccessService, { EMPTY_TEACHER_EXTENSIONS } from '../../services/TeacherAccessService';

const EMPTY_ACCESS = { extensions: EMPTY_TEACHER_EXTENSIONS, refresh: () => Promise.resolve(), status: 'idle' };
const TeacherAccessContext = createContext(EMPTY_ACCESS);
export const useTeacherAccess = () => useContext(TeacherAccessContext);
export const scopeAllowsTeacher = (scope, teacherId, assignedTeacherId) => (
  scope === 'school' || (scope === 'own' && !!teacherId && teacherId === assignedTeacherId)
);

export function TeacherAccessWarning() {
  const { t } = useTranslation();
  const { status } = useTeacherAccess();
  return status === 'error'
    ? <div role="alert" className="alert alert-warning">{t('manage:teacherExtensions.accessUnavailable')}</div>
    : null;
}

export default function TeacherAccessProvider({ user, children }) {
  const contextKey = user ? `${user.id || user.email}:${user.schoolId}:${user.role}:${user.teacherId}:${user.activeContextKey}` : '';
  const enabled = !!user && user.role === 'TEACHER' && !(user.systemRoles || []).some(role => ['ADMIN', 'OPERATOR'].includes(role));
  const generation = useRef(0);
  const [access, setAccess] = useState({ ...EMPTY_ACCESS, contextKey });
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    if (!enabled) {
      setAccess({ ...EMPTY_ACCESS, contextKey });
      return;
    }
    try {
      const next = await TeacherAccessService.getCurrentAccess();
      if (generation.current === request) setAccess({ ...next, contextKey, status: 'ready' });
    } catch (error) {
      if (generation.current === request) setAccess({ ...EMPTY_ACCESS, contextKey, status: 'error' });
    }
  }, [contextKey, enabled]);
  useEffect(() => {
    setAccess({ ...EMPTY_ACCESS, contextKey, status: enabled ? 'loading' : 'idle' });
    refresh();
    const onFocus = () => { refresh(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('teacher-access-denied', onFocus);
    return () => {
      generation.current += 1;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('teacher-access-denied', onFocus);
    };
  }, [refresh, contextKey, enabled]);
  const current = access.contextKey === contextKey ? access : EMPTY_ACCESS;
  const value = useMemo(() => ({ ...current, refresh }), [current, refresh]);
  return (
    <TeacherAccessContext.Provider value={value}>
      {children}
    </TeacherAccessContext.Provider>
  );
}

TeacherAccessProvider.propTypes = { user: PropTypes.object, children: PropTypes.node.isRequired };
TeacherAccessProvider.defaultProps = { user: null };
